import { Dom, Type } from 'main.core';
import { BaseEvent } from 'main.core.events';
import { Uploader, UploaderFile, UploaderOptions, UploaderStatus } from 'ui.file-uploader.api';
import { BitrixVue } from 'ui.vue';

import { TileUploaderComponent } from './components/tile-uploader-component';
import { TileUploaderOptions } from './tile-uploader-options';

/**
 * @memberof BX.UI.FileUploader.Widgets
 */
export default class TileUploader
{
	#uploader: Uploader = null;
	#vueApp = null;
	#items = [];
	#error = {};
	#uploaderStatus: UploaderStatus = UploaderStatus.STOPPED;
	#stackMode: boolean = false;

	constructor(uploaderOptions: UploaderOptions, tileUploaderOptions: TileUploaderOptions)
	{
		const widgetOptions = Type.isPlainObject(tileUploaderOptions) ? Object.assign({}, tileUploaderOptions) : {};
		this.#stackMode = widgetOptions.stackMode === true;

		const context = this;

		this.#vueApp = BitrixVue.createApp({
			data: () => {
				return {
					items: this.#items,
					stackMode: this.#stackMode,
					error: this.#error,
				};
			},

			components: {
				TileUploaderComponent,
			},

			beforeCreate()
			{
				this.$bitrix.Application.set(context);
			},

			// language=Vue
			template: `<TileUploaderComponent :items="items" :stackMode="stackMode" :error="error" />`,
		});

		const options = Type.isPlainObject(uploaderOptions) ? Object.assign({}, uploaderOptions) : {};
		const userEvents = options.events;
		options.events = {
			'File:onAddStart': this.handleFileAdd.bind(this),
			'File:onRemove': this.handleFileRemove.bind(this),
			'File:onUploadProgress': this.handleFileUploadProgress.bind(this),
			'File:onStateChange': this.handleFileStateChange.bind(this),
			'File:onError': this.handleFileError.bind(this),
			'onError': this.handleError.bind(this),
		};

		this.#uploader = new Uploader(options);
		this.#uploader.subscribeFromOptions(userEvents);
	}

	getUploader(): Uploader
	{
		return this.#uploader;
	}

	renderTo(node: HTMLElement): void
	{
		if (Type.isDomNode(node))
		{
			const container = Dom.create('div');
			node.appendChild(container);

			if (!this.getUploader().getHiddenFieldsContainer())
			{
				this.getUploader().setHiddenFieldsContainer(node);
			}

			this.#vueApp.mount(container);
		}
	}

	remove(id): void
	{
		this.getUploader().removeFile(id);
	}

	getItem(id)
	{
		return this.#items.find(item => item.id === id);
	}

	createItemFromFile(file: UploaderFile): { [key: string]: any }
	{
		const item = file.getState();
		item.progress = 0;

		return item;
	}

	handleFileAdd(event: BaseEvent): void
	{
		const { file, error } = event.getData();
		this.#items.push(this.createItemFromFile(file));
	}

	handleFileError(event: BaseEvent): void
	{
		const { file, error } = event.getData();

		const item = this.getItem(file.getId());
		item.error = error;
	}

	handleFileRemove(event: BaseEvent): void
	{
		const { file } = event.getData();

		const position = this.#items.findIndex(fileInfo => fileInfo.id === file.getId());
		if (position >= 0)
		{
			this.#items.splice(position, 1);
		}
	}

	handleFileUploadProgress(event: BaseEvent): void
	{
		const { file, progress } = event.getData();
		const item = this.getItem(file.getId());
		if (item)
		{
			item.progress = progress;
		}
	}

	handleFileStateChange(event: BaseEvent): void
	{
		const { file } = event.getData();
		const item = this.getItem(file.getId());
		if (item)
		{
			Object.assign(item, file.getState());
		}
	}

	handleError(event: BaseEvent): void
	{
		Object.assign(this.#error, event.getData().error.toJSON());
	}
}


class StackUploader extends TileUploader
{
	constructor(uploaderOptions: UploaderOptions, tileUploaderOptions: TileUploaderOptions)
	{
		super(uploaderOptions, tileUploaderOptions);


		this.getVue().setTemplate('');
	}
}
